import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { logError } from '@sequencehq/utils'
import { useMemo, useState } from 'react'
import { filenameFromResponse, getPortalDomain } from '@sequencehq/utils'
import { getEnvironment } from 'lib/environment/environment'
import { useNavigate } from 'react-router-dom'
import { useRemoteQuotePdfDownload } from '@sequencehq/quote-content'
import { dashboardv20240509Client } from '@sequencehq/api/dashboard/v20240509'

export enum Status {
  Accepted = 'accepted',
  Executed = 'executed',
  Published = 'published',
  NotPublished = 'notPublished',
  Template = 'template'
}

type PublishedStatusActions = {
  visible: boolean
  disabled: boolean
  tooltip: string
  onClick: () => void
}

type UsePublishedStatus = () => {
  status: Status
  features: {
    download: PublishedStatusActions
    openBillingSchedule: PublishedStatusActions
    copyLinkAction: PublishedStatusActions & {
      onBlur: () => void
      isCopied: boolean
    }
  }
}

/**
 * @deprecated
 */
export const usePublishedStatus: UsePublishedStatus = () => {
  const cubeContext = useCubeContext()
  const navigate = useNavigate()
  const [isCopied, setIsCopied] = useState(false)

  const status = useMemo(() => {
    if (cubeContext.queries.quote.isATemplate) {
      return Status.Template
    }

    if (cubeContext.queries.rawData.data.quote.executedAt) {
      return Status.Executed
    }

    if (cubeContext.queries.rawData.data.quote.acceptedAt) {
      return Status.Accepted
    }

    if (cubeContext.queries.rawData.data.quote.publishedAt) {
      return Status.Published
    }

    return Status.NotPublished
  }, [
    cubeContext.queries.rawData.data.quote.acceptedAt,
    cubeContext.queries.rawData.data.quote.executedAt,
    cubeContext.queries.rawData.data.quote.publishedAt,
    cubeContext.queries.quote.isATemplate
  ])

  const { onClick: downloadOnClick } = useRemoteQuotePdfDownload({
    fileName: res =>
      filenameFromResponse(
        res,
        `${cubeContext.queries.rawData.data.quote.quoteNumber}.pdf`
      ),
    downloadPdfEndpoint: () =>
      dashboardv20240509Client.downloadQuotePdf({
        id: cubeContext.queries.rawData.data.common.id
      })
  })

  const download = useMemo(() => {
    const downloadEnabled = ['GENERATED', 'NOT_YET_REQUESTED'].includes(
      cubeContext.queries.rawData.data.quote.pdfStatus
    )
    return {
      visible: [Status.Published, Status.Accepted, Status.Executed].includes(
        status
      ),
      disabled: !downloadEnabled,
      tooltip: downloadEnabled ? 'Download quote PDF' : 'PDF not ready',
      onClick: downloadOnClick
    }
  }, [downloadOnClick, status])

  const portalUrl = useMemo(() => {
    const { customerId, id } = cubeContext.queries.rawData.data.common
    return `${getPortalDomain(getEnvironment())}/quotes/${customerId}/${id}`
  }, [cubeContext.queries.rawData.data.common])

  const billingScheduleUrl = useMemo(() => {
    const { billingScheduleId } = cubeContext.queries.rawData.data.quote
    return `/billing-schedules/${billingScheduleId}`
  }, [cubeContext.queries.rawData.data.quote])

  const openBillingSchedule = useMemo(() => {
    return {
      visible: status === Status.Executed,
      disabled: false,
      tooltip: 'Go to billing schedule',
      onClick: () => navigate(billingScheduleUrl)
    }
  }, [status, billingScheduleUrl, navigate])

  const copyLinkTooltipText = isCopied ? 'Link copied' : 'Copy published link'

  const copyLinkAction = useMemo(() => {
    return {
      visible: [Status.Published, Status.Accepted, Status.Executed].includes(
        status
      ),
      disabled: false,
      tooltip: copyLinkTooltipText,
      isCopied,
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(portalUrl)
          setIsCopied(true)
          setTimeout(() => {
            setIsCopied(false)
          }, 1500)
        } catch (e) {
          logError(e)
        }
      },
      onBlur: () => setIsCopied(false)
    }
  }, [status, copyLinkTooltipText, portalUrl, isCopied])

  return {
    status,
    features: {
      download,
      openBillingSchedule,
      copyLinkAction
    }
  }
}
